import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Bell, Gauge, Play, Radar as RadarIcon, Radio, Wind, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { SITE, SOCIALS } from "../config";
import { StormDeskRadar } from "../components/StormDeskRadar";
import { fetchNWSActive, eventColor } from "../utils/nws";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatChip = ({ icon: Icon, label, value, accent = "text-cyan-400" }) => (
  <div className="glass flex items-center gap-3 rounded-xl px-4 py-3">
    <Icon className={`h-4 w-4 shrink-0 ${accent}`} />
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mono truncate text-sm font-semibold">{value}</div>
    </div>
  </div>
);

const SectionHead = ({ eyebrow, eyebrowIcon: EyebrowIcon, eyebrowClass = "text-cyan-400", title, note, link, linkLabel, linkTestId }) => (
  <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
    <div>
      <div className={`mono flex items-center gap-1.5 text-[11px] uppercase tracking-widest ${eyebrowClass}`}>
        {EyebrowIcon && <EyebrowIcon className="h-3.5 w-3.5" />} {eyebrow}
      </div>
      <h2 className="display-italic sd-title-gradient mt-1 text-3xl uppercase sm:text-4xl">{title}</h2>
      {note && <p className="mt-1 text-sm text-slate-400">{note}</p>}
    </div>
    {link && (
      <Link to={link} data-testid={linkTestId} className="inline-flex items-center gap-1 text-sm text-slate-300 transition-colors hover:text-white">
        {linkLabel} <ArrowRight className="h-4 w-4" />
      </Link>
    )}
  </div>
);

const Home = () => {
  const [streams, setStreams] = useState([]);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const [rs, ra] = await Promise.all([
          axios.get(`${API}/streams?live=true`).catch(() => ({ data: [] })),
          fetchNWSActive(),
        ]);
        if (cancel) return;
        setStreams((rs.data || []).filter((s) => s.live));
        setAlerts(ra || []);
      } catch (_) {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  const topAlerts = alerts.slice(0, 4);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      {/* HERO */}
      <section className="pt-12 pb-10">
        <div className="mono inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/5 px-3 py-1.5 text-[11px] uppercase tracking-widest text-red-300">
          <span className="live-dot h-1.5 w-1.5 rounded-full bg-red-500" /> Live · Severe Weather Network
        </div>
        <h1 className="display-italic mt-5 text-[48px] uppercase leading-[0.9] sm:text-6xl lg:text-7xl">
          <span className="sd-title-gradient">THE STORM</span><br />
          <span className="sd-blue-gradient">IS ON AIR.</span>
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-400">{SITE.description}</p>
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <Link to="/watch"><Button className="h-11 gap-2 bg-red-600 px-5 text-white hover:bg-red-500"><Play className="h-4 w-4" /> Watch Live</Button></Link>
          <Link to="/radar" data-testid="home-open-radar-link">
            <Button variant="outline" className="h-11 gap-2 border-white/15 bg-white/[0.03] px-5 text-slate-100 hover:bg-white/[0.08]"><RadarIcon className="h-4 w-4 text-cyan-400" /> Open Radar</Button>
          </Link>
        </div>
        <div className="mt-8 grid max-w-3xl grid-cols-2 gap-2.5 sm:grid-cols-4">
          <StatChip icon={Radio} label="On Air" value={`${streams.length} feeds`} accent="text-red-400" />
          <StatChip icon={Zap} label="NWS Alerts" value={`${alerts.length} active`} accent="text-amber-400" />
          <StatChip icon={Wind} label="Data" value="NWS / NOAA" />
          <StatChip icon={Gauge} label="Radar" value="Storm Desk" />
        </div>
      </section>

      {/* LIVE RADAR */}
      <section className="mt-4">
        <SectionHead
          eyebrow="Storm Desk 24/7"
          eyebrowIcon={RadarIcon}
          eyebrowClass="text-red-400"
          title="Live radar"
          note="Interactive radar with live National Weather Service alerts."
          link="/radar"
          linkLabel="Full radar"
          linkTestId="home-open-full-radar-link"
        />
        <div className="overflow-hidden rounded-2xl">
          <StormDeskRadar height={520} />
        </div>
      </section>

      {/* ACTIVE ALERTS */}
      <section className="mt-14" data-testid="home-alerts-section">
        <SectionHead
          eyebrow="National Weather Service"
          eyebrowIcon={Bell}
          eyebrowClass="text-amber-400"
          title="Active alerts"
          note={`${alerts.length} products active across the United States right now.`}
          link="/alerts"
          linkLabel="View all alerts"
          linkTestId="home-view-all-alerts-link"
        />
        {topAlerts.length === 0 ? (
          <div className="sd-panel rounded-2xl p-8 text-center">
            <Zap className="mx-auto h-8 w-8 text-emerald-400 opacity-80" />
            <div className="mt-3 font-semibold text-slate-100">No active NWS alerts</div>
            <div className="mt-1 text-sm text-slate-400">Quiet skies. This panel refreshes every 30 seconds.</div>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {topAlerts.map((a, i) => (
              <Link
                key={a.id || i}
                to="/alerts"
                data-testid={`home-alert-card-${i}`}
                className="sd-panel rounded-xl border-l-2 p-4 transition-colors hover:bg-white/[0.04]"
                style={{ borderLeftColor: eventColor(a.event).fill }}
              >
                <div className="mono flex items-center gap-2 text-[10px] uppercase tracking-widest" style={{ color: eventColor(a.event).fill }}>
                  <AlertTriangle className="h-3.5 w-3.5" /> {a.severity || "Alert"}
                </div>
                <div className="mt-2 text-sm font-semibold leading-snug text-slate-100">{a.event}</div>
                <div className="mt-1 line-clamp-2 text-xs text-slate-400">{a.areaDesc}</div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ON AIR */}
      <section className="mt-14" data-testid="home-streams-section">
        <SectionHead
          eyebrow="On air now"
          eyebrowIcon={Radio}
          eyebrowClass="text-red-400"
          title="Live feeds"
          link="/watch"
          linkLabel="Live broadcast"
        />
        {streams.length === 0 ? (
          <div className="sd-panel flex flex-wrap items-center justify-between gap-3 rounded-xl px-5 py-4">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Chaser video</div>
              <div className="mt-0.5 text-sm font-semibold text-slate-100">OFF AIR — no live feed right now</div>
            </div>
            <span className="mono text-[10px] uppercase tracking-widest text-slate-500">Radar above stays live 24/7</span>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {streams.map((s) => (
              <Link key={s.id} to={`/watch/${s.id}`} className="group relative overflow-hidden rounded-xl border border-white/5 bg-black/40 transition-colors hover:border-red-500/50">
                <div className="relative aspect-video">
                  {s.poster ? (
                    <img src={s.poster} alt={s.title} className="h-full w-full object-cover opacity-85 transition-opacity group-hover:opacity-100" />
                  ) : (
                    <div className="grid h-full w-full place-items-center bg-gradient-to-br from-slate-900 to-slate-950 text-slate-500">
                      <Radio className="h-10 w-10 opacity-60" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                  <div className="absolute left-3 top-3 flex items-center gap-2">
                    <Badge className="gap-1.5 border-0 bg-red-600 text-white hover:bg-red-600">
                      <span className="live-dot h-1.5 w-1.5 rounded-full bg-white" /> LIVE
                    </Badge>
                    <Badge variant="outline" className="mono border-white/20 bg-black/40 text-slate-200">{s.tag || (s.kind || "LIVE").toUpperCase()}</Badge>
                  </div>
                  <div className="absolute inset-x-3 bottom-3">
                    <div className="truncate text-sm font-semibold text-slate-100">{s.title}</div>
                    <div className="truncate text-xs text-slate-400">{s.chaser ? s.chaser + " · " : ""}{s.location || ""}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* FOLLOW */}
      <section className="mt-14 mb-4">
        <div className="sd-panel flex flex-wrap items-center justify-between gap-4 rounded-2xl px-5 py-4">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Follow the coverage</div>
            <div className="mt-0.5 text-sm text-slate-300">Storm Desk posts warnings, chases, and recaps on every platform.</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {SOCIALS.map((s) => (
              <a
                key={s.key}
                href={s.href}
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`radar-simulcast-${s.key}-link`}
                className="mono border border-white/10 bg-black/40 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-200 transition-colors hover:bg-white/10 hover:text-white"
              >
                {s.name}
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
